import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { isLikelyWebView, isNativeDriveApp, isStandaloneDisplay } from "../../lib/drive/bridge";
import { storageGet, storageSet } from "../../lib/drive/storage";

export const DRIVE_INSTALL_DISMISSED_KEY = "drive-install-dismissed";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

function isIosSafari(ua) {
  return /(iPhone|iPad|iPod)/.test(ua) && /Safari\//.test(ua) && !/(CriOS|FxiOS|EdgiOS)/.test(ua);
}

/**
 * Invito a installare Drive sulla schermata Home.
 * - Mai nell'app nativa, nelle WebView o se già installata (display-mode standalone).
 * - Android/desktop Chromium: usa l'evento beforeinstallprompt (prompt nativo).
 * - iOS Safari: nessun evento, mostra le istruzioni "Condividi → Aggiungi a Home".
 * - "Non ora" lo nasconde per 7 giorni.
 * - `enabled=false` (es. pagina di login): l'evento viene comunque catturato ma l'invito
 *   compare solo dopo, così non copre il modulo di accesso.
 */
export default function DriveInstallPrompt({ enabled = true }) {
  const [deferred, setDeferred] = useState(null);
  const [iosHint, setIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    if (isNativeDriveApp(ua) || isLikelyWebView(ua) || isStandaloneDisplay()) return undefined;
    const dismissed = parseInt(storageGet(DRIVE_INSTALL_DISMISSED_KEY) || "0", 10);
    if (dismissed && Date.now() - dismissed < SNOOZE_MS) return undefined;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    if (isIosSafari(ua)) {
      setIosHint(true);
      setVisible(true);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!visible || !enabled) return null;

  const dismiss = () => {
    storageSet(DRIVE_INSTALL_DISMISSED_KEY, String(Date.now()));
    setVisible(false);
  };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } finally {
      setDeferred(null);
      setVisible(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-labelledby="drive-install-title"
      data-testid="drive-install-prompt"
      className="fixed inset-x-3 z-50 mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-4 text-slate-900 shadow-2xl dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
      style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
    >
      <div className="flex items-start gap-3">
        <img src="/drive/icons/drive-192.png" alt="" className="h-12 w-12 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p id="drive-install-title" className="text-base font-semibold">Installa Drive</p>
          {iosHint && !deferred ? (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Tocca <Share className="inline h-4 w-4 align-text-bottom" aria-label="Condividi" /> e poi
              <strong> Aggiungi a Home</strong> per aprire il giro a schermo intero.
            </p>
          ) : (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Apri il giro del giorno dalla schermata Home, a schermo intero.</p>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Chiudi"
          className="-m-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-500 active:bg-slate-100 dark:active:bg-slate-700"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={dismiss} className="h-11 flex-1 rounded-xl border border-slate-200 text-base font-medium dark:border-slate-600">
          Non ora
        </button>
        {deferred ? (
          <button
            type="button"
            onClick={install}
            data-testid="drive-install-button"
            className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-indigo-600 text-base font-semibold text-white active:bg-indigo-700"
          >
            <Download className="h-5 w-5" /> Installa
          </button>
        ) : null}
      </div>
    </div>
  );
}
